import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from '@shared/lib/toast';
import { logger } from '@shared/lib/logger';
import { t } from '@shared/i18n';
import { normalizeError } from './normalizeError';

/** Surface any query/mutation failure as a toast + scrubbed log (auth handled by redirect). */
function surface(error: unknown): void {
  const appError = normalizeError(error);
  logger.error('request failed', { kind: appError.kind, status: appError.status });
  if (appError.kind === 'auth') return; // hardLogout already redirects
  // Every 403 seen from this API is a role/permission denial (a view-only member attempting a
  // change), but the backend's own wording is written per-endpoint — e.g. delete/update-settle
  // answers "You cannot change wallet entries in this group…", worded for a wallet-balance
  // rejection, not a role one, and reads wrong here. Route every 403 through the ONE friendly
  // message instead of trusting whichever raw text a given endpoint happens to send — this is
  // the single global chokepoint every mutation's error passes through, so it covers every
  // current and future call site, not just the ones that remembered to special-case it locally.
  const message = appError.status === 403 ? t.common.viewOnlyPermission : appError.messageForUser;
  toast.error(message);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        const { kind, status } = normalizeError(error);
        // 403 no longer classes as 'auth' (it must toast), but retrying a permission
        // rejection can never succeed — keep it out of the retry loop explicitly.
        return kind !== 'auth' && kind !== 'validation' && status !== 403 && failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
  queryCache: new QueryCache({ onError: surface }),
  mutationCache: new MutationCache({ onError: surface }),
});
